import { Button } from "@package/subframe/components/Button";
import { TextField } from "@package/subframe/components/TextField";
import { useState } from "react";
import { toast } from "sonner";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { sanitizePin } from "@/features/device-setup/pin-login-api";

import { changeOwnPin, interpretChangePinError } from "./change-pin-api";

const PIN_LENGTH = 4;
const MISMATCH_MESSAGE = "PINs do not match.";
const INCORRECT_MESSAGE = "Incorrect PIN. Please try again.";
const LOCKED_MESSAGE = "Account locked. Ask your manager to reset your PIN.";
const GENERIC_ERROR = "Something went wrong. Please try again.";

/**
 * Self-service PIN change, shown in settings for shared-device PIN
 * sessions in place of password management. The user proves they know their
 * current PIN and picks a new one.
 */
export function ChangePinCard() {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit =
    currentPin.length === PIN_LENGTH &&
    newPin.length === PIN_LENGTH &&
    confirmPin.length === PIN_LENGTH &&
    !isSubmitting;

  function onFieldChange(setter: (value: string) => void) {
    return (value: string) => {
      setter(sanitizePin(value, PIN_LENGTH));
      setError(null);
    };
  }

  async function handleSubmit() {
    if (!canSubmit) return;

    if (newPin !== confirmPin) {
      setNewPin("");
      setConfirmPin("");
      setError(MISMATCH_MESSAGE);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await changeOwnPin(currentPin, newPin);
      setCurrentPin("");
      setNewPin("");
      setConfirmPin("");
      toast.success("PIN updated successfully.");
    } catch (err) {
      const result = interpretChangePinError(err);
      switch (result.kind) {
        case "locked":
          setCurrentPin("");
          setNewPin("");
          setConfirmPin("");
          setError(LOCKED_MESSAGE);
          break;
        case "invalid":
          setCurrentPin("");
          setError(INCORRECT_MESSAGE);
          break;
        default:
          setError(GENERIC_ERROR);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Change PIN</CardTitle>
        <CardDescription>
          The 4-digit PIN you use to log in on shared devices
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex max-w-sm flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <TextField className="h-auto w-full" label="Current PIN">
            <TextField.Input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder="Enter your current PIN"
              value={currentPin}
              disabled={isSubmitting}
              onChange={(e) => onFieldChange(setCurrentPin)(e.target.value)}
            />
          </TextField>

          <TextField className="h-auto w-full" label="New PIN">
            <TextField.Input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder="Enter a new 4-digit PIN"
              value={newPin}
              disabled={isSubmitting}
              onChange={(e) => onFieldChange(setNewPin)(e.target.value)}
            />
          </TextField>

          <TextField
            className="h-auto w-full"
            label="Confirm new PIN"
            error={!!error}
            helpText={error ?? undefined}
          >
            <TextField.Input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder="Re-enter your new PIN"
              value={confirmPin}
              disabled={isSubmitting}
              onChange={(e) => onFieldChange(setConfirmPin)(e.target.value)}
            />
          </TextField>

          <div>
            <Button
              type="submit"
              variant="brand-primary"
              disabled={!canSubmit}
              loading={isSubmitting}
            >
              Update PIN
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
